/**
 * WebSocket client wrapper with reconnect (exponential backoff + jitter),
 * heartbeats, and a hard cap on retry attempts so a doomed connection
 * (expired token, terminal session, blocked WAF rule) doesn't loop
 * forever in the SPA.
 *
 * Frame format mirrors the SessionRoom DO contract.
 */

export type ChatFrame =
  | { t: 'msg'; id: string; role: 'agent' | 'customer'; name?: string | null; body: string; at: number }
  | { t: 'system'; kind: 'participant_joined' | 'participant_left' | string; role?: string }
  | { t: 'policy.violation'; id: string; reason: string }
  | {
      t: 'file.ready'
      id: string
      filename?: string | null
      mime?: string | null
      size?: number | null
      uploaderRole?: 'customer' | 'agent' | null
    }
  | {
      t: 'file.rejected'
      id: string
      filename?: string | null
      reason: string
    }
  /**
   * Ephemeral typing indicator — server forwards typing.start/stop
   * frames between peers, augmenting with the sender's role + name.
   * Never persisted. Receiver UI auto-hides at 6s defensively.
   */
  | {
      t: 'typing'
      state: 'start' | 'stop'
      role: 'agent' | 'customer'
      name?: string | null
    }
  | { t: 'room.expired' }
  | { t: 'pong'; at: number }

type ClientFrame =
  | { t: 'msg'; id: string; body: string }
  | { t: 'leave' }
  | { t: 'ping' }
  | { t: 'typing'; state: 'start' | 'stop' }

interface Hooks {
  onFrame: (f: ChatFrame) => void
  onOpen?: () => void
  onClose?: (code: number) => void
  onError?: (err: unknown) => void
  /**
   * Fired exactly once when the connection has failed too many times in a
   * row to be worth retrying. The Chatroom should show a "Session ended"
   * UI in response — there is no point in another retry.
   */
  onGiveUp?: (reason: 'too-many-failures' | 'expired') => void
}

const HEARTBEAT_MS = 30_000
const MAX_BACKOFF_MS = 30_000
// 6 attempts at 0.5/1/2/4/8/16s ≈ ~30s of trying before giving up.
const MAX_FAILED_ATTEMPTS = 6

export interface WsConn {
  send(f: ClientFrame): void
  close(): void
}

export function connectRoomWs(sessionId: string, token: string | null, hooks: Hooks): WsConn {
  let ws: WebSocket | null = null
  let closed = false
  let backoff = 500
  let consecutiveFailures = 0
  let everConnected = false
  let heartbeat: ReturnType<typeof setInterval> | null = null
  let givenUp = false

  function open() {
    if (closed || givenUp) return
    const proto = location.protocol === 'https:' ? 'wss' : 'ws'
    const url = `${proto}://${location.host}/ws/session/${encodeURIComponent(sessionId)}` +
      (token ? `?t=${encodeURIComponent(token)}` : '')
    ws = new WebSocket(url)

    ws.addEventListener('open', () => {
      backoff = 500
      consecutiveFailures = 0
      everConnected = true
      heartbeat = setInterval(() => ws?.send(JSON.stringify({ t: 'ping' })), HEARTBEAT_MS)
      hooks.onOpen?.()
    })
    ws.addEventListener('message', (ev) => {
      try {
        const f = JSON.parse(String(ev.data)) as ChatFrame
        hooks.onFrame(f)
        if (f.t === 'room.expired') {
          // Session is over, server-side. Stop retrying.
          givenUp = true
          hooks.onGiveUp?.('expired')
          ws?.close(1000, 'expired')
        }
      } catch (e) {
        hooks.onError?.(e)
      }
    })
    ws.addEventListener('error', (e) => hooks.onError?.(e))
    ws.addEventListener('close', (ev) => {
      if (heartbeat) clearInterval(heartbeat)
      heartbeat = null
      hooks.onClose?.(ev.code)

      // Normal closure (1000) and the explicit "expired" path don't retry.
      if (closed || givenUp || ev.code === 1000) return

      consecutiveFailures += 1
      if (consecutiveFailures >= MAX_FAILED_ATTEMPTS) {
        givenUp = true
        hooks.onGiveUp?.('too-many-failures')
        return
      }
      reconnect()
    })
  }

  function reconnect() {
    if (closed || givenUp) return
    // Jitter the backoff window — base * [0.5, 1.5).
    const jittered = Math.min(MAX_BACKOFF_MS, backoff) * (0.5 + Math.random())
    setTimeout(open, jittered)
    backoff = Math.min(MAX_BACKOFF_MS, backoff * 2)
  }

  function send(f: ClientFrame) {
    if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify(f))
  }

  function close() {
    closed = true
    if (heartbeat) clearInterval(heartbeat)
    ws?.close(1000, 'client closing')
  }

  // Surface to the caller whether this connection has ever been live —
  // exposed via a side-effect because WsConn intentionally stays small.
  void everConnected

  open()
  return { send, close }
}
